import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYMENT_LINK = process.env.REACT_APP_STRIPE_PAYMENT_LINK;
const MAX_POLL_ATTEMPTS = 30;
const POLL_INTERVAL_MS = 2000;

const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export const useWorkbookPayment = () => {
  const [product, setProduct] = useState(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [downloadToken, setDownloadToken] = useState(null);
  const [polling, setPolling] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'paid' | 'pending' | 'expired'

  // Track mount state so polling never setState's on an unmounted component
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    axios.get(`${API}/ebook/product`).then((r) => setProduct(r.data)).catch(() => {});
  }, []);

  // Self-recursive via setTimeout — guarded by mountedRef so unmount halts polling.
  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (!mountedRef.current) return;
    if (attempts >= MAX_POLL_ATTEMPTS) {
      setPolling(false);
      setPaymentStatus("pending");
      toast.error("Still processing. Please check your email shortly.");
      return;
    }
    try {
      const r = await axios.get(`${API}/ebook/status/${sessionId}`);
      if (!mountedRef.current) return;
      if (r.data.payment_status === "paid") {
        setDownloadToken(r.data.download_token);
        setPaymentStatus("paid");
        setPolling(false);
        toast.success("Payment confirmed. Your workbook is ready.");
        return;
      }
      if (r.data.status === "expired") {
        setPaymentStatus("expired");
        setPolling(false);
        toast.error("Payment session expired. Please try again.");
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), POLL_INTERVAL_MS);
    } catch (e) {
      if (!mountedRef.current) return;
      const code = e?.response?.status;
      // 404 = webhook hasn't landed yet; 5xx = transient — keep polling either way
      if (code === 404 || code >= 500) {
        setTimeout(() => pollStatus(sessionId, attempts + 1), POLL_INTERVAL_MS);
        return;
      }
      setPolling(false);
      toast.error("Could not verify payment.", { duration: 8000 });
    }
  }, []);

  // Detect return from Stripe via ebook_session_id query param.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("ebook_session_id");
    if (sid) {
      setPolling(true);
      setPaymentStatus("pending");
      pollStatus(sid);
      // Clean URL but keep #workbook hash
      window.history.replaceState({}, "", window.location.pathname + "#workbook");
      // Ensure the buyer actually sees the confirmation UI
      setTimeout(() => {
        document.getElementById("workbook")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    }
  }, [pollStatus]);

  const onEmailChange = (v) => {
    setEmail(v);
    setEmailError("");
  };

  const onPurchase = async (e) => {
    e.preventDefault();
    setEmailError("");
    if (!validateEmail(email)) {
      setEmailError("Enter a valid email so we can deliver the workbook.");
      return;
    }
    setSubmitting(true);
    if (PAYMENT_LINK) {
      window.location.href = `${PAYMENT_LINK}?prefilled_email=${encodeURIComponent(email)}`;
      return;
    }
    try {
      const r = await axios.post(`${API}/ebook/checkout`, {
        product_id: "workbook_intro",
        email,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setSubmitting(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something interrupted checkout. Please try again.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return {
    product,
    email,
    emailError,
    submitting,
    downloadToken,
    polling,
    paymentStatus,
    onEmailChange,
    onPurchase,
  };
};
